import React, { useEffect, useState, useContext } from 'react';
import { dateConverter, parseProcessingStatus, parseTrackingStatus, timestampConverter } from '../../helpers';
import { useNavigate } from 'react-router';
import useWindowSize from '../../hooks/useWindowSize';
import { useTranslation } from 'react-i18next';
import { CurrentTeamContext } from '../../contexts/CurrentTeamContext';
import Loading from '../../components/Loading/Loading';
import Button from '../../components/Button';
import {
	ArrowNarrowRightIcon,
	CloseIcon,
	CloseXIcon,
	DocketDotsVerIcon,
	DownloadIcon,
	TopnavMenuIcon,
} from '../../components/SVGIcons/SVGIcons';
import EmptyState from '../../components/EmptyState/EmptyState';
import { itemLabelTemp } from '../../templates/templates';
import Axios from 'axios';
import config from '../../config';
import { useModalsGlobal } from '../../hooks/useModalsGlobal';
import { ModalContext } from '../../contexts/ModalContext';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import { defaultDropStrategy, DNDContext, registerDropStrategy } from '../../contexts/DNDContext';
import { useDNDColumns } from '../../hooks/useDNDColumns';

export default function ItemsList(props) {
	const { listItems, pack, loading, reload } = props || {};
	const [lastMousePress, setLastMousePress] = useState(null);
	const [newMousePress, setNewMousePress] = useState(null);
	const [loadingEnrichment, setLoadingEnrichment] = useState(false);
	const [enrichedListItems, setEnrichedListItems] = useState([]);

	let navigate = useNavigate();

	const { showModal, hideModal } = useModalsGlobal();
	const { flashFadeModal } = useContext(ModalContext);

	const { teamState } = useContext(CurrentTeamContext);
	const { DNDState, DNDDispatch } = useContext(DNDContext);

	//! When this becomes package listing instead of only details,
	//! we need to remake this so the packages append their own
	//! dropable areas much like the orders in the sidebar
	const defaultColumn = {
		id: pack?.packageUuid,
		type: 'item',
		fields: [],
		immutable: false,
		reorderable: false,
	};

	const [column] = useDNDColumns([pack?.packageUuid]);

	const { width } = useWindowSize();

	function onTouchStart(e) {
		setLastMousePress({ x: e.touches[0].clientX, y: e.touches[0].clientY });
		setNewMousePress({ x: e.touches[0].clientX, y: e.touches[0].clientY });
	}

	function onTouchMove(e) {
		setNewMousePress({ x: e.touches[0].clientX, y: e.touches[0].clientY });
	}

	function onTouchEnd(e, item) {
		if (newMousePress?.x === lastMousePress?.x && newMousePress?.y === lastMousePress?.y) {
			navigate(`/packages/${item.packageUuid}`);
		}
	}

	function getFilePath(file) {
		let res = Axios({
			url: `${config.api}/file?path=${file.path}`,
			method: 'GET',
			responseType: 'blob',
		}).then((response) => {
			const url = window.URL.createObjectURL(response.data);
			return url;
		});
		return res;
	}

	async function removeItem(itemUuid) {
		const data = {
			packageUuid: 'unset',
		};

		try {
			const res = await Axios({
				url: `${config.apiv1}/order/item.update/${itemUuid}`,
				method: 'PATCH',
				data: data,
			});

			if (res.data?.ok) {
				flashFadeModal('Order item removed successfully');

				const newColumn = {
					...column,
					fields: column.fields.filter((field) => field.id !== itemUuid),
				};
				DNDDispatch({
					type: 'MERGE_COLUMNS',
					columns: [newColumn],
				});
			}
		} catch (err) {
			flashFadeModal('Error when removing item');
			return err;
		}
	}

	function removeItemPrompt(itemUuid) {
		showModal('confirmChoice', {
			callback: () => removeItem(itemUuid),
			header: 'Are you sure you want to remove this item?',
			open: true,
		});
	}

	useEffect(() => {
		if (listItems) {
			let members = teamState?.currentTeam?.members?.filter((member) => member.firstname && member.lastname);
			let enrichedListItems = [...listItems];

			enrichedListItems.forEach((item, index) => {
				let matchingMember = members.filter((member) => member.userUuid === item.userUuid);
				item.user = matchingMember[0];
				item.id = item.itemUuid;
			});

			setEnrichedListItems(enrichedListItems);

			let initialColumn = column || defaultColumn;

			DNDDispatch({
				type: 'MERGE_COLUMNS',
				columns: [{ ...initialColumn, fields: enrichedListItems }],
			});
		}

		setLoadingEnrichment(false);
	}, [listItems]);

	useEffect(() => {
		DNDDispatch({ type: 'SET_DROP_TYPE', dropType: 'default' });
		registerDropStrategy('default', defaultDropStrategy);
	}, [column]);

	return (
		<div className='listOuterCont'>
			<div className='list'>
				<div className='listHeader'>
					<h4 className='hidden' style={{ width: '12%' }}></h4>
					<h4>Order ref.</h4>
					<h4 style={{ width: '100%' }}>Description</h4>
					{/* <h4>Buyer</h4> */}
					{/* <h4>Description</h4> */}
					<h4 style={{ width: '100%' }}>Order hint</h4>
					<h4 style={{ width: '100%' }}>Note</h4>
					<h4>
						<Button
							style='tertiary-secondary'
							maxWidth={width > 1028 ? 140 : 40}
							minWidth={width > 1028 ? 140 : 40}
							disabled
						/>
					</h4>
					<h4 style={{ width: '12%' }}></h4>
				</div>
				{!loading && column ? (
					<>
						<Droppable droppableId={column.id} isDropDisabled={column.immutable === true}>
							{(provided) => (
								<div
									className={`dropSection${DNDState?.showDropZones === true ? ' dropZone' : ''}`}
									ref={provided.innerRef}
									{...provided.droppableProps}>
									{column.fields.map((field, key) => {
										return (
											<React.Fragment key={field.id}>
												<Draggable draggableId={field.id} index={key}>
													{(provided) => (
														<div
															ref={provided.innerRef}
															{...provided.draggableProps}
															className='item'
															onTouchStart={onTouchStart}
															onTouchMove={onTouchMove}
															onTouchEnd={(e) => onTouchEnd(e, field)}>
															<p className='data hidden' style={{ width: '12%' }}>
																<Button
																	size='md'
																	style='tertiary-secondary'
																	iconLeft={<DocketDotsVerIcon />}
																	maxWidth={40}
																	minWidth={40}
																	dragHandleProps={provided.dragHandleProps}
																/>
															</p>
															<p className='data'>
																<a href={`/orders/${field.orderUuid}`}>
																	{field.orderRef || ''}
																</a>
															</p>
															<p className='data' style={{ width: '100%' }}>
																{field.itemDesc || ''}
															</p>
															{/* <p className='data'>{field.buyerUserName || ''}</p> */}
															{/* <p className='data'>{field.description || ''}</p> */}
															<p className='data' style={{ width: '100%' }}>
																{field.buyerHint || field.sellerHint || ''}
															</p>
															<p className='data' style={{ width: '100%' }}>
																{field.note || ''}
															</p>
															<p className='data'>
																<Button
																	text={width > 1028 ? 'Download label' : null}
																	size='md'
																	style='tertiary-secondary'
																	iconLeft={<DownloadIcon />}
																	maxWidth={width > 1028 ? 160 : 40}
																	minWidth={width > 1028 ? 160 : 40}
																	onClick={() => {
																		itemLabelTemp(field, getFilePath, true, {
																			x: 6.5,
																			y: 3.5,
																		});
																	}}
																/>
															</p>
															<p className='data' style={{ width: '12%' }}>
																{teamState?.currentTeam?.teamUuid ===
																pack.senderTeamUuid ? (
																	<Button
																		size='md'
																		style='tertiary-secondary'
																		iconLeft={<CloseXIcon />}
																		maxWidth={40}
																		minWidth={40}
																		onClick={() => {
																			removeItemPrompt(field.id);
																		}}
																	/>
																) : (
																	<></>
																)}
															</p>
														</div>
													)}
												</Draggable>
											</React.Fragment>
										);
									})}

									{provided.placeholder}
								</div>
							)}
						</Droppable>
					</>
				) : (
					<Loading type='circle' />
				)}
			</div>
		</div>
	);
}

// {listItems && listItems?.length !== 0 ? (
// 	<>
// 		{listItems.map((item, key) => {
// 			return (
// 				<div
// 					key={key}
// 					className='item'
// 					onTouchStart={onTouchStart}
// 					onTouchMove={onTouchMove}
// 					onTouchEnd={(e) => onTouchEnd(e, item)}>
// 					<p className='data'>{item.orderRef || ''}</p>
// 					{/* <p className='data'>{item.buyerUserName || ''}</p> */}
// 					{/* <p className='data'>{item.description || ''}</p> */}
// 					<p className='data'>{item.note || ''}</p>
// 					<p className='data'>
// 						<Button
// 							text={width > 1028 ? 'Label' : null}
// 							size='md'
// 							style='tertiary-secondary'
// 							iconLeft={<DownloadIcon />}
// 							maxWidth={width > 1028 ? 140 : 40}
// 							minWidth={width > 1028 ? 140 : 40}
// 							onClick={() => {
// 								itemLabelTemp(item, getFilePath, true, { x: 6.5, y: 3.5 });
// 							}}
// 						/>
// 					</p>
// 					<p className='data'>
// 						{teamState?.currentTeam?.teamUuid === pack.senderTeamUuid ? (
// 							<Button
// 								size='md'
// 								style='tertiary-secondary'
// 								iconLeft={<CloseXIcon />}
// 								maxWidth={40}
// 								minWidth={40}
// 								onClick={() => {
// 									removeItemPrompt(item.itemUuid);
// 								}}
// 							/>
// 						) : (
// 							<></>
// 						)}
// 					</p>
// 				</div>
// 			);
// 		})}
// 	</>
// ) : (
// 	<EmptyState header='No items in package' />
// )}
